import { useRequiredContext } from "@redotech/react-util/context";
import { LoadState, useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { usePoll } from "@redotech/react-util/poll";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { createAttachmentUpload } from "@redotech/redo-merchant-app-common/client/team";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { BisImportProgressSchema } from "@redotech/redo-merchant-marketing-rpc/schema/back-in-stock/list-bis-imports";
import { toast } from "@redotech/redo-web/alert";
import {
  FileUploadState,
  RedoFileUpload,
} from "@redotech/redo-web/arbiter-components/file-upload/redo-file-upload";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoProgressBar } from "@redotech/redo-web/arbiter-components/progress-bar/redo-progress-bar";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { parseBisCsv, ParseBisCsvResult } from "../../../utils/parse-bis-csv";
import * as styles from "./upload-bis-modal.module.css";

const POLL_INTERVAL = Temporal.Duration.from({ seconds: 2 });

interface UploadBisModalProps {
  open: boolean;
  onClose: () => void;
  onUploadComplete: () => void;
}

export const UploadBisModal = memo(function UploadBisModal({
  open,
  onClose,
  onUploadComplete,
}: UploadBisModalProps) {
  const marketingRpcClient = useRequiredContext(RedoMarketingRpcClientContext);

  const importsLoad = usePoll(
    async () => {
      if (!open) {
        return undefined;
      }
      const { imports } = await marketingRpcClient.listBisImports({});
      return imports.find(
        (imp) => imp.status === "preparing" || imp.status === "processing",
      );
    },
    POLL_INTERVAL,
    [open, marketingRpcClient],
  );

  return (
    <UploadBisModalContent
      importsLoad={importsLoad}
      onClose={onClose}
      onUploadComplete={onUploadComplete}
      open={open}
    />
  );
});

interface UploadBisModalContentProps {
  open: boolean;
  onClose: () => void;
  onUploadComplete: () => void;
  importsLoad: LoadState<BisImportProgressSchema | undefined>;
}

const UploadBisModalContent = memo(function UploadBisModalContent({
  open,
  onClose,
  onUploadComplete,
  importsLoad,
}: UploadBisModalContentProps) {
  const [file, setFile] = useState<File | null>(null);

  const merchantClient = useRequiredContext(RedoMerchantClientContext);
  const marketingRpcClient = useRequiredContext(RedoMarketingRpcClientContext);

  const parseResultLoad = useLoad(async () => {
    if (!file) {
      return undefined;
    }
    try {
      return await parseBisCsv(file);
    } catch {
      toast("Failed to parse CSV file", { variant: "error" });
      return undefined;
    }
  }, [file]);
  const parseResult = parseResultLoad.value;

  const runningImport = importsLoad.value;

  // Detect completion: when runningImport transitions from existing to null
  const prevRunningImportRef = useRef(runningImport);
  useEffect(() => {
    const prev = prevRunningImportRef.current;
    prevRunningImportRef.current = runningImport;

    // If we had a running import and now we don't, it completed
    if (prev && !runningImport && !importsLoad.pending && open) {
      const messages: string[] = [];
      if (prev.created > 0) {
        messages.push(`${prev.created.toLocaleString()} subscriptions created`);
      }
      if (prev.duplicatesSkipped > 0) {
        messages.push(
          `${prev.duplicatesSkipped.toLocaleString()} duplicates skipped`,
        );
      }
      if (prev.invalidRowsSkipped > 0) {
        messages.push(
          `${prev.invalidRowsSkipped.toLocaleString()} invalid rows skipped`,
        );
      }
      toast(messages.join(", ") || "Import complete", { variant: "success" });
      onUploadComplete();
    }
  }, [runningImport, importsLoad.pending, open, onUploadComplete]);

  const [uploadLoad, triggerUpload] = useTriggerLoad(async () => {
    if (!file) {
      return;
    }
    try {
      const { url } = await createAttachmentUpload(merchantClient, { file });
      const s3Key = url.replace(/^https?:\/\/[^/]+\//, "");
      await marketingRpcClient.startBisImport({ s3Key, filename: file.name });
      setFile(null);
    } catch (error) {
      console.error("Error starting BIS import:", error);
      toast("Failed to start import", { variant: "error" });
      throw error;
    }
  });
  const uploading = uploadLoad.pending;

  const handleClose = () => {
    if (uploading || runningImport) {
      toast("Import is running in the background", { variant: "info" });
    }
    setFile(null);
    onClose();
  };

  const handleFilesAdded = (files: FileList | null) => {
    if (files && files.length > 0) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      setFile(files[0]);
    }
  };

  const handleFileDeleted = () => {
    setFile(null);
  };

  const hasSubscriptions = parseResult && parseResult.subscriptions.length > 0;
  const isImportRunning = uploading || !!runningImport;

  if (importsLoad.pending) {
    return (
      <ModalWrapper
        hasSubscriptions={false}
        isImportRunning={false}
        onClose={handleClose}
        open={open}
        pending
        triggerUpload={triggerUpload}
        uploading={false}
      >
        <Flex align="center" justify="center" p="lg">
          <RedoSpinner size="sm" />
        </Flex>
      </ModalWrapper>
    );
  }

  if (runningImport) {
    const progressPercent = Math.round(runningImport.progress);
    return (
      <ModalWrapper
        hasSubscriptions={false}
        isImportRunning
        onClose={handleClose}
        open={open}
        pending={false}
        triggerUpload={triggerUpload}
        uploading={uploading}
      >
        <Flex dir="column" gap="sm">
          <Text fontSize="sm" fontWeight="medium">
            {runningImport.filename}
          </Text>
          <RedoProgressBar percentage={progressPercent} />
          <Text fontSize="sm" textColor="secondary">
            {runningImport.status === "preparing"
              ? "Preparing import..."
              : runningImport.totalRows > 0
                ? `Processing: ${runningImport.processedRows.toLocaleString()} / ${runningImport.totalRows.toLocaleString()} (${progressPercent}%)`
                : "Starting import..."}
          </Text>
          {(runningImport.created > 0 ||
            runningImport.duplicatesSkipped > 0) && (
            <Text fontSize="xs" textColor="secondary">
              {runningImport.created.toLocaleString()} created,{" "}
              {runningImport.duplicatesSkipped.toLocaleString()} duplicates
              skipped
              {runningImport.invalidRowsSkipped > 0 &&
                `, ${runningImport.invalidRowsSkipped.toLocaleString()} invalid rows`}
            </Text>
          )}
        </Flex>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper
      hasSubscriptions={!!hasSubscriptions}
      isImportRunning={isImportRunning}
      onClose={handleClose}
      open={open}
      pending={false}
      triggerUpload={triggerUpload}
      uploading={uploading}
    >
      <UploadForm
        file={file}
        onFileDeleted={handleFileDeleted}
        onFilesAdded={handleFilesAdded}
        parseResult={parseResult}
      />
    </ModalWrapper>
  );
});

interface ModalWrapperProps {
  open: boolean;
  onClose: () => void;
  triggerUpload: () => void;
  uploading: boolean;
  pending: boolean;
  hasSubscriptions: boolean;
  isImportRunning: boolean;
  children: ReactNode;
}

const ModalWrapper = memo(function ModalWrapper({
  open,
  onClose,
  triggerUpload,
  uploading,
  pending,
  hasSubscriptions,
  isImportRunning,
  children,
}: ModalWrapperProps) {
  return (
    <RedoModal
      isOpen={open}
      onModalCloseRequested={onClose}
      primaryButton={{
        text: isImportRunning ? "Importing..." : "Start Import",
        onClick: triggerUpload,
        size: "sm",
        pending: uploading,
        disabled: pending || !hasSubscriptions || isImportRunning,
      }}
      secondaryButton={{
        text: isImportRunning ? "Close" : "Cancel",
        onClick: onClose,
        size: "sm",
      }}
      title="Upload Back-in-Stock Subscriptions"
    >
      <Flex dir="column" gap="xl" w="full">
        {children}
      </Flex>
    </RedoModal>
  );
});

interface UploadFormProps {
  file: File | null;
  onFilesAdded: (files: FileList | null) => void;
  onFileDeleted: () => void;
  parseResult: ParseBisCsvResult | undefined;
}

const UploadForm = memo(function UploadForm({
  file,
  onFilesAdded,
  onFileDeleted,
  parseResult,
}: UploadFormProps) {
  const fileUploadState: FileUploadState<File>[] = useMemo(() => {
    if (!file) {
      return [];
    }
    return [{ value: file, name: file.name, bytesTotal: file.size }];
  }, [file]);

  const hasErrors = parseResult && parseResult.errors.length > 0;
  const hasSubscriptions = parseResult && parseResult.subscriptions.length > 0;

  return (
    <>
      <RedoFileUpload
        accept="text/csv"
        acceptMessage="CSV"
        files={fileUploadState}
        fullWidth
        handleFileDeleted={onFileDeleted}
        handleFilesAdded={onFilesAdded}
        nFilesToAccept={1}
      />

      <Flex dir="column" gap="xs">
        <Text fontSize="sm" textColor="secondary">
          The CSV must have these column headers:
        </Text>
        <ul className={styles.columnList}>
          <li>
            <Text as="span" fontSize="sm" fontWeight="medium">
              Email
            </Text>
          </li>
          <li>
            <Text as="span" fontSize="sm" fontWeight="medium">
              Product ID
            </Text>{" "}
            <Text as="span" fontSize="sm" textColor="secondary">
              (Shopify numeric ID)
            </Text>
          </li>
          <li>
            <Text as="span" fontSize="sm" fontWeight="medium">
              Variant ID
            </Text>{" "}
            <Text as="span" fontSize="sm" textColor="secondary">
              (Shopify numeric ID)
            </Text>
          </li>
          <li>
            <Text as="span" fontSize="sm" fontWeight="medium">
              Created
            </Text>{" "}
            <Text as="span" fontSize="sm" textColor="secondary">
              (optional, preserves original date)
            </Text>
          </li>
        </ul>
      </Flex>

      {parseResult && (
        <Flex dir="column" gap="md">
          {hasSubscriptions && (
            <Text fontSize="sm" textColor="primary">
              Found {parseResult.subscriptions.length.toLocaleString()} valid
              subscription
              {parseResult.subscriptions.length !== 1 ? "s" : ""}
              {parseResult.duplicateCount > 0 &&
                ` (${parseResult.duplicateCount.toLocaleString()} duplicate${parseResult.duplicateCount !== 1 ? "s" : ""} in file removed)`}
            </Text>
          )}

          {hasErrors && (
            <Flex dir="column" gap="sm">
              <Text fontSize="sm" textColor="secondary">
                {parseResult.errors.length.toLocaleString()} rows will be
                skipped:
              </Text>
              <Flex
                dir="column"
                gap="xs"
                style={{ maxHeight: "150px", overflowY: "auto" }}
              >
                {parseResult.errors.slice(0, 10).map((error, index) => (
                  <Text fontSize="xs" key={index} textColor="secondary">
                    {error}
                  </Text>
                ))}
                {parseResult.errors.length > 10 && (
                  <Text fontSize="xs" textColor="secondary">
                    ...and {parseResult.errors.length - 10} more
                  </Text>
                )}
              </Flex>
            </Flex>
          )}
        </Flex>
      )}
    </>
  );
});
